import axios from 'axios';
import connectionService from './ConnectionService';
import AuthService from './AuthService';

const PRODUCTS_KEY = 'pos-products';
const SESSION_KEY = 'pos-session';
const API_CACHE_PREFIX = 'pos-cache-';

const OfflineService = {
    isOnline() {
        return connectionService.getConnectionStatus();
    },

    saveSessionInfo(sessionData, configId, loginDate) {
        const cleanedSessionData = {
            ...sessionData,
            products: sessionData.products?.map(product => ({
                ...product,
                image_base64: null
            }))
        };

        localStorage.setItem(
            SESSION_KEY,
            JSON.stringify({
                sessionData: cleanedSessionData,
                configId: configId,
                loginDate: loginDate,
                timestamp: Date.now(),
            })
        );
    },

    getSessionInfo() {
        const sessionInfo = localStorage.getItem(SESSION_KEY);
        if (!sessionInfo) return { sessionData: null, configId: null, loginDate: null };

        try {
            const data = JSON.parse(sessionInfo);
            return {
                sessionData: data.sessionData,
                configId: data.configId,
                loginDate: data.loginDate,
            };
        } catch (error) {
            console.error('Error al parsear la informacion de sesion:', error);
            return { sessionData: null, configId: null, loginDate: null };
        }
    },

    clearSessionInfo() {
        localStorage.removeItem(SESSION_KEY);
    },

    saveProducts(products) {
        const cleanedProducts = products?.map(product => ({
            ...product,
            image_base64: null
        }));

        localStorage.setItem(
            PRODUCTS_KEY,
            JSON.stringify({
                products: cleanedProducts,
                timestamp: Date.now(),
            })
        );
    },

    getProducts() {
        const productsJson = localStorage.getItem(PRODUCTS_KEY);
        if (!productsJson) return [];

        try {
            const data = JSON.parse(productsJson);
            return data.products || [];
        } catch (error) {
            console.error('Error al parsear productos:', error);
            return [];
        }
    },

    clearProducts() {
        localStorage.removeItem(PRODUCTS_KEY);
    },

    cacheApiResponse(url, data) {
        const cacheKey = `${API_CACHE_PREFIX}${url}`;
        localStorage.setItem(
            cacheKey,
            JSON.stringify({
                data: data,
                timestamp: Date.now(),
            })
        );
    },

    getCachedApiResponse(url) {
        const cacheKey = `${API_CACHE_PREFIX}${url}`;
        const cachedJson = localStorage.getItem(cacheKey);

        if (!cachedJson) return null;

        try {
            const cached = JSON.parse(cachedJson);
            const isRecent = Date.now() - cached.timestamp < 24 * 60 * 60 * 1000;
            return isRecent ? cached.data : null;
        } catch (error) {
            console.error('Error al parsear cache de API:', error);
            return null;
        }
    },

    clearCachedApiResponse(url) {
        const cacheKey = `${API_CACHE_PREFIX}${url}`;
        localStorage.removeItem(cacheKey);
    },

    async validateSession(pin) {
        console.log('Validando sesion con PIN...');

        const user = AuthService.getUser();
        const userId = user ? user.user_id : null;

        if (!userId) {
            throw new Error('No hay usuario autenticado');
        }

        console.log('Usando usuario autenticado ID:', userId);

        // Se valida a través del ApiProxy local (igual que el PDV), NO directo a Odoo. El
        // ApiProxy tiene un CACHÉ de sesión y keepAlive hacia Odoo, así que casi nunca falla:
        // la llamada del totem al ApiProxy es local (localhost) y no depende de una petición
        // directa a Odoo desde el navegador (que a veces se cuelga aunque Odoo sí responda).
        // /pos_validate_session reusa la sesión existente, así que reintentar es seguro; se
        // reintenta ante 500 (conectividad con Odoo) o error de red; solo el PIN incorrecto
        // (401) falla de inmediato.
        const API_URL = 'http://localhost:9000';
        const MAX_INTENTOS = 3;
        let ultimoError = null;

        for (let intento = 1; intento <= MAX_INTENTOS; intento++) {
            try {
                console.log(`Enviando validacion al ApiProxy (intento ${intento}/${MAX_INTENTOS})...`);

                const response = await axios.post(
                    `${API_URL}/api/pos/sessions/validate`,
                    {
                        pin: pin,
                        user_id: userId
                    },
                    {
                        timeout: 20000,
                        headers: {
                            'Content-Type': 'application/json',
                        }
                    }
                );

                // El ApiProxy envuelve la respuesta de Odoo en { success, data, source }.
                const body = response.data || {};
                const result = body.data || body;

                if (body.success && result && result.authenticated) {
                    console.log(`Sesion validada exitosamente (${body.source || 'odoo'})`);
                    console.log('POS encontrado:', result.config_name);
                    console.log('Session ID:', result.session_id);
                    return result;
                }
                throw new Error(body.error || result?.error || 'Error en validacion de sesion');

            } catch (error) {
                // PIN incorrecto (401) = rechazo real → no reintentar.
                if (error.response && error.response.status === 401) {
                    throw new Error('PIN incorrecto o POS no configurado');
                }
                // 500 (conectividad con Odoo) o sin respuesta (red local) → se reintenta.
                ultimoError = error;
                const detalle = error.response
                    ? (error.response.data?.error || error.response.data?.message || error.response.statusText)
                    : error.message;
                console.warn(`Validacion intento ${intento} falló (${detalle})` +
                    (intento < MAX_INTENTOS ? '; reintentando...' : ''));
                if (intento < MAX_INTENTOS) {
                    await new Promise(r => setTimeout(r, 800));
                }
            }
        }

        console.error('Error validando sesion tras reintentos:', ultimoError?.message);
        throw new Error('No se pudo validar la sesión (revisa la conexión). Intenta de nuevo.');
    },

    async closeSession(sessionId, pin, confirm = false) {
        console.log(`${confirm ? 'Cerrando' : 'Obteniendo datos de'} sesion...`);
        console.log('Parametros:', { sessionId, pin, confirm });

        const user = AuthService.getUser();
        const userId = user ? user.user_id : null;

        if (userId) {
            console.log('Usando usuario autenticado ID:', userId);
        }

        try {
            console.log('Enviando operacion a Odoo...');

            const formData = new URLSearchParams();
            formData.append('session_id', sessionId.toString());
            formData.append('pin', pin);
            formData.append('confirm', confirm ? 'true' : 'false');
            if (userId) {
                formData.append('user_id', userId.toString());
            }

            const response = await axios.post(
                `/pos_close_session`,
                formData,
                {
                    timeout: 30000,
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    }
                }
            );

            const result = response.data;

            if (result && result.success) {
                console.log(`${confirm ? 'Sesion cerrada' : 'Datos obtenidos'} exitosamente`);
                return result;
            } else {
                throw new Error(result?.error || 'Error en operacion de sesion');
            }

        } catch (error) {
            console.error('Error en operacion:', error.message);

            if (error.response && error.response.data) {
                throw new Error(error.response.data.error || 'Error en el servidor');
            } else {
                throw new Error('Error de conectividad con el servidor');
            }
        }
    }
};

export default OfflineService;